import React, { useState, useEffect } from 'react';
import { useAuth, useAuthPrompt } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Wallet,
  Plus,
  Edit2,
  Trash2,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Loader2,
  PieChart,
  Target,
  X,
  Calendar,
  Bell
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const defaultCategories = [
  { name: 'Food & Dining', icon: '🍔', color: 'hsl(38 90% 55%)' },
  { name: 'Shopping', icon: '🛍️', color: 'hsl(280 60% 55%)' },
  { name: 'Transportation', icon: '🚗', color: 'hsl(200 80% 55%)' },
  { name: 'Entertainment', icon: '🎬', color: 'hsl(340 80% 55%)' },
  { name: 'Utilities', icon: '💡', color: 'hsl(160 60% 50%)' },
  { name: 'Healthcare', icon: '🏥', color: 'hsl(0 70% 55%)' },
  { name: 'Personal', icon: '👤', color: 'hsl(210 50% 55%)' },
  { name: 'Other', icon: '📦', color: 'hsl(0 0% 50%)' }
];

export default function Budgets() {
  const { isAuthenticated, user } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  
  const [budgets, setBudgets] = useState([]);
  const [spending, setSpending] = useState({});
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [newBudget, setNewBudget] = useState({ category: '', limit: '', alert_threshold: 80 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [budgetsRes, summaryRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/personal/budgets`, { withCredentials: true }),
        axios.get(`${API_URL}/api/personal/budget-summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/personal/budget-alerts`, { withCredentials: true })
      ]);
      
      setBudgets(budgetsRes.data.budgets || []);
      setSummary(summaryRes.data.summary || null);
      setSpending(summaryRes.data.breakdown?.reduce((acc, b) => {
        acc[b.category] = b.spent;
        return acc;
      }, {}) || {});
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      // Use demo data if not authenticated
      setBudgets([
        { id: '1', category: 'Food & Dining', limit: 600, alert_threshold: 80 },
        { id: '2', category: 'Shopping', limit: 400, alert_threshold: 80 },
        { id: '3', category: 'Transportation', limit: 300, alert_threshold: 80 },
        { id: '4', category: 'Entertainment', limit: 200, alert_threshold: 80 }
      ]);
      setSpending({
        'Food & Dining': 425,
        'Shopping': 380,
        'Transportation': 150,
        'Entertainment': 220
      });
      setSummary({
        total_budget: 1500,
        total_spent: 1175,
        total_remaining: 325,
        days_remaining: 6,
        daily_budget: 54.17
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditBudget = async () => {
    if (!isAuthenticated) {
      showAuthPrompt('Save Budget');
      return;
    }
    if (!newBudget.category || !newBudget.limit) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      if (editingBudget) {
        await axios.put(`${API_URL}/api/personal/budgets/${editingBudget.id}`, {
          category: newBudget.category,
          limit: parseFloat(newBudget.limit),
          alert_threshold: parseFloat(newBudget.alert_threshold) || 80
        }, { withCredentials: true });
        toast.success('Budget updated!');
      } else {
        await axios.post(`${API_URL}/api/personal/budgets`, {
          category: newBudget.category,
          limit: parseFloat(newBudget.limit),
          alert_threshold: parseFloat(newBudget.alert_threshold) || 80
        }, { withCredentials: true });
        toast.success('Budget created!');
      }
      setShowAddModal(false);
      setEditingBudget(null);
      setNewBudget({ category: '', limit: '', alert_threshold: 80 });
      fetchData();
    } catch (error) {
      toast.error('Failed to save budget');
    }
  };

  const handleEditBudget = (budget) => {
    setEditingBudget(budget);
    setNewBudget({
      category: budget.category,
      limit: budget.limit.toString(),
      alert_threshold: budget.alert_threshold || 80
    });
    setShowAddModal(true);
  };

  const handleDeleteBudget = async (budgetId) => {
    if (!isAuthenticated) {
      showAuthPrompt('Delete Budget');
      return;
    }
    if (!window.confirm('Delete this budget?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/personal/budgets/${budgetId}`, { withCredentials: true });
      toast.success('Budget deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete budget');
    }
  };

  const totalBudget = summary?.total_budget || budgets.reduce((sum, b) => sum + (b.limit || 0), 0);
  const totalSpent = summary?.total_spent || Object.values(spending).reduce((sum, v) => sum + v, 0);
  const totalRemaining = summary?.total_remaining || (totalBudget - totalSpent);

  const getCategoryMeta = (name) => {
    return defaultCategories.find(c => c.name === name) || defaultCategories[defaultCategories.length - 1];
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="budgets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
            Monthly <span className="text-[hsl(var(--accent))]">Budgets</span>
          </h1>
          <p className="text-muted-foreground mt-1">Set spending limits and track your progress</p>
        </div>
        <Button onClick={() => { setEditingBudget(null); setNewBudget({ category: '', limit: '', alert_threshold: 80 }); setShowAddModal(true); }} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90">
          <Plus className="w-4 h-4 mr-2" /> Add Budget
        </Button>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.slice(0, 3).map((alert, idx) => (
            <div 
              key={idx}
              className={`p-3 rounded-lg flex items-center gap-3 ${
                alert.severity === 'high' ? 'bg-red-500/10 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/20'
              }`}
            >
              <AlertTriangle className={`w-5 h-5 ${alert.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
              <div className="flex-1">
                <p className="font-medium">{alert.category}</p>
                <p className="text-sm text-muted-foreground">{alert.message}</p>
              </div>
              <Badge variant={alert.severity === 'high' ? 'destructive' : 'secondary'}>
                {alert.percentage}%
              </Badge>
            </div>
          ))}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Budget</p>
                <p className="text-2xl font-bold">{formatCurrency(totalBudget)}</p>
              </div>
              <div className="p-3 rounded-xl bg-[hsl(var(--accent))]/10">
                <Target className="w-6 h-6 text-[hsl(var(--accent))]" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Spent</p>
                <p className="text-2xl font-bold text-red-500">{formatCurrency(totalSpent)}</p>
              </div>
              <div className="p-3 rounded-xl bg-red-500/10">
                <TrendingDown className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Remaining</p>
                <p className={`text-2xl font-bold ${totalRemaining >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {formatCurrency(totalRemaining)}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${totalRemaining >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                {totalRemaining >= 0 ? (
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Daily Budget</p>
                <p className="text-2xl font-bold text-blue-400">{formatCurrency(summary?.daily_budget || 0)}</p>
                <p className="text-xs text-muted-foreground">{summary?.days_remaining || 0} days left</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <Calendar className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Budget List */}
      <div className="space-y-4">
        {budgets.length > 0 ? (
          budgets.map((budget) => {
            const spent = spending[budget.category] || 0;
            const percentage = budget.limit > 0 ? Math.min((spent / budget.limit) * 100, 100) : 0;
            const isOverBudget = spent > budget.limit;
            const isNearLimit = percentage >= (budget.alert_threshold || 80) && !isOverBudget;
            const meta = getCategoryMeta(budget.category);
            
            return (
              <Card key={budget.id} className="card-premium">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">{meta.icon}</div>
                      <div>
                        <h3 className="font-semibold">{budget.category}</h3>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(spent)} of {formatCurrency(budget.limit)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isOverBudget && (
                        <Badge variant="destructive" className="text-xs">
                          <AlertTriangle className="w-3 h-3 mr-1" /> Over Budget
                        </Badge>
                      )}
                      {isNearLimit && (
                        <Badge className="bg-amber-500/20 text-amber-500 border-0 text-xs">
                          <Bell className="w-3 h-3 mr-1" /> Near Limit
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditBudget(budget)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBudget(budget.id)}
                        className="text-muted-foreground hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="h-3 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: isOverBudget ? 'hsl(0 70% 55%)' : isNearLimit ? 'hsl(38 90% 55%)' : meta.color
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{Math.round(percentage)}% used</span>
                      <span>{formatCurrency(Math.max(budget.limit - spent, 0))} left</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="card-premium">
            <CardContent className="py-12 text-center text-muted-foreground">
              <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No budgets set</p>
              <p className="text-sm mt-1">Create your first budget to start tracking spending</p>
              <Button onClick={() => setShowAddModal(true)} className="mt-4">
                <Plus className="w-4 h-4 mr-2" /> Create Budget
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Budget Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{editingBudget ? 'Edit Budget' : 'Create Budget'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddModal(false); setEditingBudget(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Category</Label>
                <select
                  value={newBudget.category}
                  onChange={(e) => setNewBudget({ ...newBudget, category: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1"
                  disabled={!!editingBudget}
                >
                  <option value="">Select category</option>
                  {defaultCategories.map((cat) => (
                    <option key={cat.name} value={cat.name}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Monthly Limit</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="500"
                    value={newBudget.limit}
                    onChange={(e) => setNewBudget({ ...newBudget, limit: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>Alert Threshold (%)</Label>
                <div className="relative mt-1">
                  <Bell className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="80"
                    min="50"
                    max="100"
                    value={newBudget.alert_threshold}
                    onChange={(e) => setNewBudget({ ...newBudget, alert_threshold: e.target.value })}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Get alerted when spending reaches this %</p>
              </div>
              <Button onClick={handleAddOrEditBudget} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90">
                {editingBudget ? 'Update Budget' : 'Create Budget'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
